import { create } from 'zustand';
import type { CustomerType } from '@matix/types';

/**
 * Customer picker UI state.
 *
 * Note: the list of customers and individual customer detail no longer live
 * here. They come from the live API via TanStack Query (see features/customers/
 * hooks.ts). This store only tracks transient picker state — what the user is
 * typing, which mode they're in, which row is selected — so opening/closing
 * the modal can reset it cleanly.
 */

export type SearchMode = 'phone' | 'name';
export type PickerView = 'picker' | 'add';

interface CustomerPickerState {
  view: PickerView;
  /** Cash vs credit source. Cashier toggles between them at the top of the picker. */
  sourceType: CustomerType;
  /** phone (numpad) vs name (keyboard) search mode within the chosen source. */
  searchMode: SearchMode;
  searchText: string;
  selectedId: number | null;
  /** When non-null, the add-view is in "edit" mode for this customer. */
  editingId: number | null;
  /**
   * What the NEXT open starts on instead of the blank picker — the phone-system
   * link (lib/customerCallLink) opens straight onto the caller. Applied and
   * cleared by resetPicker, which the modal runs as it opens.
   */
  pendingSeed: PickerSeed | null;

  setView: (v: PickerView) => void;
  setSourceType: (t: CustomerType) => void;
  setSearchMode: (m: SearchMode) => void;
  setSearchText: (s: string) => void;
  appendDigit: (digit: string) => void;
  backspace: () => void;
  clearSearch: () => void;
  selectId: (id: number | null) => void;
  setEditingId: (id: number | null) => void;
  resetPicker: () => void;
  seedNextOpen: (seed: PickerSeed) => void;
}

export type PickerSeed = Partial<Pick<CustomerPickerState, 'view' | 'searchMode' | 'searchText' | 'selectedId'>>;

export const useCustomerPicker = create<CustomerPickerState>((set) => ({
  view: 'picker',
  sourceType: 'cash',
  searchMode: 'name',
  searchText: '',
  selectedId: null,
  editingId: null,
  pendingSeed: null,

  setView: (view) => set({ view }),
  setSourceType: (sourceType) =>
    set({ sourceType, searchText: '', selectedId: null }),
  setSearchMode: (searchMode) =>
    set({ searchMode, searchText: '', selectedId: null }),
  setSearchText: (searchText) => set({ searchText, selectedId: null }),
  appendDigit: (d) => set((s) => ({ searchText: s.searchText + d, selectedId: null })),
  backspace: () => set((s) => ({ searchText: s.searchText.slice(0, -1), selectedId: null })),
  clearSearch: () => set({ searchText: '', selectedId: null }),
  selectId: (selectedId) => set({ selectedId }),
  setEditingId: (editingId) => set({ editingId }),

  resetPicker: () =>
    set((s) => ({
      view: 'picker',
      sourceType: 'cash',
      searchMode: 'name',
      searchText: '',
      selectedId: null,
      editingId: null,
      ...s.pendingSeed,
      pendingSeed: null,
    })),
  seedNextOpen: (pendingSeed) => set({ pendingSeed }),
}));

/** Tier display label. */
export function tierLabel(tier: 'new' | 'bronze' | 'silver' | 'gold' | 'platinum'): string {
  if (tier === 'new') return 'New';
  return tier.charAt(0).toUpperCase() + tier.slice(1);
}
